import { useEffect, useState } from "react";
import Nav from "@/components/Nav";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Trash2, PlusCircle, Save } from "lucide-react";
import { useNavigate } from "react-router-dom";

const EMPTY = { couple: "", initials: "", city: "", quote: "", photo_url: "", tone: "gold" };

export default function AdminTestimonials() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && !user.is_admin) {
      toast.error("Admin only");
      nav("/dashboard");
    }
  }, [user, nav]);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/testimonials");
      setItems(data.testimonials || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    }
  };
  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });

  const create = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/testimonials", draft);
      toast.success("Testimonial added");
      setDraft(EMPTY);
      load();
    } catch { toast.error("Failed to save"); }
    finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    toast.success("Deleted");
    load();
  };

  const saveEdit = async (t) => {
    try {
      await api.put(`/admin/testimonials/${t.id}`, {
        couple: t.couple, initials: t.initials, city: t.city, quote: t.quote,
        photo_url: t.photo_url || "", tone: t.tone || "gold",
      });
      toast.success("Updated");
      load();
    } catch { toast.error("Update failed"); }
  };

  const editField = (id, k, v) => setItems(items.map(t => t.id === id ? { ...t, [k]: v } : t));

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Nav />
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
        <div className="overline">Admin</div>
        <h1 className="font-serif-display text-4xl text-[#1E1B4B] mt-2">Testimonials CMS</h1>
        <p className="mt-3 text-stone-600">Add, edit, or remove couple stories that appear on the landing page.</p>

        {/* Create */}
        <form onSubmit={create} className="mt-8 border border-stone-200 rounded-2xl bg-white p-6">
          <div className="flex items-center gap-2"><PlusCircle className="text-amber-700" size={18} /><div className="overline">Add new</div></div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <input data-testid="admin-couple-input" required value={draft.couple} onChange={upd("couple")} placeholder="Couple (e.g., Rohit & Priyanka)" className="border border-stone-300 rounded-xl px-4 py-3 bg-white" />
            <input data-testid="admin-initials-input" required value={draft.initials} onChange={upd("initials")} placeholder="Initials (e.g., R&P)" className="border border-stone-300 rounded-xl px-4 py-3 bg-white" />
            <input data-testid="admin-city-input" required value={draft.city} onChange={upd("city")} placeholder="City (e.g., Bengaluru → Pune)" className="border border-stone-300 rounded-xl px-4 py-3 bg-white" />
            <input data-testid="admin-photo-input" value={draft.photo_url} onChange={upd("photo_url")} placeholder="Photo URL (optional)" className="border border-stone-300 rounded-xl px-4 py-3 bg-white" />
            <select data-testid="admin-tone-input" value={draft.tone} onChange={upd("tone")} className="border border-stone-300 rounded-xl px-4 py-3 bg-white">
              <option value="gold">Gold avatar</option>
              <option value="indigo">Indigo avatar</option>
            </select>
            <textarea data-testid="admin-quote-input" required value={draft.quote} onChange={upd("quote")} placeholder="Testimonial quote" rows={3} className="md:col-span-2 border border-stone-300 rounded-xl px-4 py-3 bg-white" />
          </div>
          <button data-testid="admin-create-btn" disabled={loading} className="btn-pill mt-4 text-white text-sm px-5 py-2.5" style={{ background: "#1E1B4B" }}>
            {loading ? "Saving…" : "Publish testimonial"}
          </button>
        </form>

        {/* List */}
        <div className="mt-10 space-y-4">
          <div className="overline">Existing ({items.length})</div>
          {items.map((t) => (
            <div key={t.id} data-testid={`admin-testimonial-${t.id}`} className="border border-stone-200 rounded-2xl bg-white p-5">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <input value={t.couple} onChange={(e) => editField(t.id, "couple", e.target.value)} className="border border-stone-200 rounded-lg px-3 py-2 text-sm" />
                <input value={t.initials} onChange={(e) => editField(t.id, "initials", e.target.value)} className="border border-stone-200 rounded-lg px-3 py-2 text-sm" />
                <input value={t.city} onChange={(e) => editField(t.id, "city", e.target.value)} className="border border-stone-200 rounded-lg px-3 py-2 text-sm" />
                <input value={t.photo_url || ""} onChange={(e) => editField(t.id, "photo_url", e.target.value)} placeholder="Photo URL" className="border border-stone-200 rounded-lg px-3 py-2 text-sm" />
              </div>
              <textarea value={t.quote} onChange={(e) => editField(t.id, "quote", e.target.value)} rows={2} className="mt-3 w-full border border-stone-200 rounded-lg px-3 py-2 text-sm" />
              <div className="mt-3 flex items-center gap-2">
                <button onClick={() => saveEdit(t)} className="btn-pill text-sm text-white px-4 py-2 flex items-center gap-1" style={{ background: "#1E1B4B" }}><Save size={14} /> Save</button>
                <button onClick={() => remove(t.id)} className="btn-pill text-sm text-red-700 border border-red-200 px-4 py-2 flex items-center gap-1 hover:bg-red-50"><Trash2 size={14} /> Delete</button>
                <div className="ml-auto text-xs text-stone-400">{new Date(t.created_at).toLocaleDateString()}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
